/**
 * The three-step create-alias wizard shell: step header, current step, and
 * navigation.
 *
 * Presentational. All state lives in {@link useCreateAliasWizard}, which the
 * modal owns so a failed create can drop the user back on the password step with
 * their username and display name intact.
 *
 * Forward navigation only ever happens through `Next`, because each of the first
 * two steps requires a server verdict. The step triggers therefore move
 * backwards only.
 */

import { useTranslation } from 'react-i18next';
import { Steps } from '@ark-ui/react';
import { Button } from '../../components/Button';
import { Spinner } from '../../components/Spinner';
import { MIN_PASSPHRASE_LENGTH } from '../identityModalUtils';
import type { CreateAliasWizardState } from '../../hooks/useCreateAliasWizard';
import { CREATE_ALIAS_STEPS } from '../../hooks/useCreateAliasWizard';
import { CreateUsernameStep } from './CreateUsernameStep';
import { CreateDisplayNameStep } from './CreateDisplayNameStep';
import { CreatePasswordStep } from './CreatePasswordStep';

export interface CreateAliasWizardProps {
  wizard: CreateAliasWizardState;
  /** Reveal state for the passphrase field, owned by the modal so it resets with the view. */
  passwordVisible: boolean;
  onPasswordVisibleChange: (visible: boolean) => void;
  /** Invoked on the final step once the passphrase is confirmed. */
  onSubmit: () => void;
  /** Leaves the wizard from the first step. */
  onCancel: () => void;
  submitting: boolean;
}

export function CreateAliasWizard({
  wizard,
  passwordVisible,
  onPasswordVisibleChange,
  onSubmit,
  onCancel,
  submitting,
}: CreateAliasWizardProps) {
  const { t } = useTranslation();

  const stepTitles = [
    t('identity.create.stepUsername'),
    t('identity.create.stepDisplayName'),
    t('identity.create.stepPassword'),
  ];

  const isLastStep = wizard.step === CREATE_ALIAS_STEPS.length - 1;
  const passphrasesMatch =
    wizard.passphrase.length >= MIN_PASSPHRASE_LENGTH &&
    wizard.passphrase === wizard.passphraseConfirm;

  const submitDisabled = isLastStep
    ? submitting || !passphrasesMatch
    : !wizard.canAdvance || wizard.checking;

  return (
    <form
      className="identity-modal-form identity-wizard"
      onSubmit={(e) => {
        e.preventDefault();
        if (submitDisabled) return;
        if (isLastStep) onSubmit();
        else void wizard.advance();
      }}
    >
      <Steps.Root
        className="identity-wizard-steps"
        count={CREATE_ALIAS_STEPS.length}
        step={wizard.step}
        onStepChange={(details) => {
          // Only backwards. Advancing requires a name-check round trip, which
          // `Next` owns.
          if (details.step < wizard.step) wizard.back();
        }}
        orientation="horizontal"
      >
        <Steps.List className="identity-wizard-steps-list">
          {stepTitles.map((title, index) => (
            <Steps.Item className="identity-wizard-steps-item" key={title} index={index}>
              <Steps.Trigger
                className="identity-wizard-steps-trigger"
                type="button"
                disabled={submitting || wizard.checking}
              >
                <Steps.Indicator className="identity-wizard-steps-indicator">
                  {index + 1}
                </Steps.Indicator>
                <span className="identity-wizard-steps-title">{title}</span>
              </Steps.Trigger>
              <Steps.Separator className="identity-wizard-steps-separator" />
            </Steps.Item>
          ))}
        </Steps.List>

        <Steps.Content className="identity-wizard-steps-content" index={0}>
          <CreateUsernameStep
            username={wizard.username}
            onUsernameChange={wizard.setUsername}
            onRotate={wizard.rotateUsername}
            canCustomize={wizard.canCustomizeUsername}
            disabled={wizard.checking || submitting}
            rejection={wizard.rejection}
            checkFailed={wizard.checkFailed}
          />
        </Steps.Content>

        <Steps.Content className="identity-wizard-steps-content" index={1}>
          <CreateDisplayNameStep
            displayName={wizard.displayName}
            onDisplayNameChange={wizard.setDisplayName}
            privacy={wizard.displayNamePrivacy}
            onPrivacyChange={wizard.setDisplayNamePrivacy}
            usernameFallback={wizard.username}
            disabled={wizard.checking || submitting}
            rejection={wizard.rejection}
            checkFailed={wizard.checkFailed}
          />
        </Steps.Content>

        <Steps.Content className="identity-wizard-steps-content" index={2}>
          <CreatePasswordStep
            passphrase={wizard.passphrase}
            onPassphraseChange={wizard.setPassphrase}
            passphraseConfirm={wizard.passphraseConfirm}
            onPassphraseConfirmChange={wizard.setPassphraseConfirm}
            visible={passwordVisible}
            onVisibleChange={onPasswordVisibleChange}
            disabled={submitting}
          />
        </Steps.Content>
      </Steps.Root>

      <div className="identity-modal-buttons">
        <Button
          type="button"
          variant="ghost"
          disabled={submitting || wizard.checking}
          onClick={() => (wizard.step === 0 ? onCancel() : wizard.back())}
        >
          {t('common.back')}
        </Button>
        <Button type="submit" variant="primary" disabled={submitDisabled}>
          {wizard.checking || submitting ? (
            <Spinner size="sm" />
          ) : isLastStep ? (
            t('identity.create.submitButton')
          ) : (
            t('common.next')
          )}
        </Button>
      </div>
    </form>
  );
}
